import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';
import axios from 'axios';

const ChangePassword = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Form state
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // User profile info from localStorage
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/HotelAdmin');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear messages when user starts typing
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const validateForm = () => {
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return false;
    }

    if (formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return false;
    }

    if (formData.currentPassword === formData.newPassword) {
      setMessage({ type: 'error', text: 'New password must be different from current password' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const authToken = localStorage.getItem('authToken');
      const userProfile = JSON.parse(localStorage.getItem('userProfile'));

      // Debug information
      console.log('=== DEBUG INFO ===');
      console.log('Auth Token exists:', !!authToken);
      console.log('User Profile:', userProfile);
      console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);
      console.log('==================');

      if (!authToken || !userProfile) {
        setMessage({ type: 'error', text: 'Authentication required. Please log in again.' });
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/hotel/admin/change-password.php`,
        {
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword
        },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `${response.data.message} You will be logged out for security.` 
        });
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Logout user after password change for security
        setTimeout(() => {
          handleLogout();
        }, 3000);
      } else {
        setMessage({ type: 'error', text: response.data.error });
      }
    } catch (error) {
      console.error('Password change error:', error);
      
      // Detailed error handling
      if (error.response?.status === 401) {
        setMessage({ type: 'error', text: 'Session expired. Please log in again.' });
        setTimeout(() => handleLogout(), 2000);
      } else if (error.response?.data?.error) {
        setMessage({ type: 'error', text: error.response.data.error });
      } else if (error.code === 'ECONNABORTED') {
        setMessage({ type: 'error', text: 'Request timeout. Please try again.' });
      } else if (error.request) {
        setMessage({ type: 'error', text: 'Network error. Please check your connection.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to change password. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1200px',
          mx: 'auto',
        }}
      >
        {/* Top Navigation */}
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        {/* Main Content Grid */}
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12} md={8} lg={6}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 4,
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  textAlign: 'center',
                  mb: 3,
                  fontSize: { xs: '24px', sm: '28px', md: '32px' }
                }}
              >
                Change Password
              </Typography>

              {message.text && (
                <Alert 
                  severity={message.type} 
                  sx={{ mb: 3 }}
                >
                  {message.text}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  disabled={loading}
                  helperText="Password must be at least 6 characters long"
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  disabled={loading}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    fontSize: '1.1rem',
                    mb: 2,
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Change Password'}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Information Panel */}
          <Grid item xs={12} md={4} lg={6}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
              }}
            >
              <Typography
                variant="h5"
                sx={{ mb: 3, fontWeight: 'bold' }}
              >
                Password Security
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  • Minimum 6 characters
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  • Different from current password
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  • Must match confirmation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  • Case sensitive
                </Typography>
              </Box>

              <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  For security, you'll be automatically logged out after changing your password.
                </Typography>
              </Box>

              {userProfile && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Current User
                  </Typography>
                  <Typography variant="body2">
                    {userProfile.first_name && userProfile.other_names 
                      ? `${userProfile.first_name} ${userProfile.other_names}`
                      : userProfile.hotel_name || userProfile.email
                    }
                  </Typography>
                  <Typography variant="body2">
                    {userProfile.department || userProfile.user_type || 'Hotel Staff'}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ChangePassword;